import {
  Autocomplete,
  Button,
  Checkbox,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  OutlinedInput,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import Modal, { CommonModalProps } from "..";
import { useState, forwardRef, useEffect } from "react";
import { DbMetadataFieldInfos } from "../../../Core/Api/Types/db-metadata-field-infos";
import ClearIcon from "@mui/icons-material/Clear";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { cacheService, orderService, setupService, userService } from "../../../Core/Api/services";
import { immerable, produce } from "immer";
import FormSection from "../../Forms/FormSection/form-section";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import { GroupAdvancedInfos } from "../../../Core/Api/Types/group-advanced-infos";
import { DbOrderPriceRuleInfos } from "../../../Core/Api/Types/db-order-price-rule-infos";
import { CurrencyTypes } from "../../../Core/Api/Types/currency-types";
import { DbOrderPriceRuleSetBaseInfos } from "../../../Core/Api/Types/db-order-price-rule-set-base-infos";
import MetadataInputIndex from "../../MetadataInput/metadata-input-index";
import AddPrice from "./add-price";
import { TranskodeVideoProfile } from "../../../Core/Api/Types/transkode-video-profile";
import { TranskodeVideoCodec } from "../../../Core/Api/Types/transkode-video-codec";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { useTranslation } from "react-i18next";

export type ModalPriceRuleElementProps = {
  priceRule: DbOrderPriceRuleInfos;
  mode: "CREATE" | "UPDATE";
  onConfirm?: () => void;
};

export type ModalPriceRuleProps = CommonModalProps<ModalPriceRuleElementProps>;

const ModalPriceRule = forwardRef<any, ModalPriceRuleProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, priceRule, mode, onConfirm } = props;
  const [newPriceRule, setNewPriceRule] = useState<DbOrderPriceRuleInfos>(priceRule);
  const [updatedRuleSets, setUpdatedRuleSets] = useState<DbOrderPriceRuleSetBaseInfos[]>(priceRule.ruleSet || []);
  const [currencies, setCurrencies] = useState<CurrencyTypes[]>();
  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyTypes | null>();
  const [groups, setGroups] = useState<GroupAdvancedInfos[]>([]);
  const [videoProfiles, setVideoProfiles] = useState<TranskodeVideoProfile[]>([]);
  const [videoCodecs, setVideoCodecs] = useState<TranskodeVideoCodec[]>([]);
  const [assetTypes, setAssetTypes] = useState<ObjectType[]>([]);
  const [productTypes, setProductTypes] = useState<OrderProductType[]>([]);
  const [selectedCondition, setSelectedCondition] = useState<any | null>(null);
  const [rowCurrencySelected, setRowCurrencySelected] = useState<
    {
      row: DbOrderPriceRuleSetBaseInfos;
      curr: any;
    }[]
  >([]);

  let metadataFromStorage: DbMetadataFieldInfos[] = cacheService.GetMetadata();
  const [metas, setMetas] = useState<{
    asset: DbMetadataFieldInfos[];
    content: DbMetadataFieldInfos[];
    title: DbMetadataFieldInfos[];
  }>();

  const [ruleTypes] = useState<{ label: string; value: string }[]>([
    {
      label: t("field:label.additionalAudioLanguage"),
      value:
        "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleAdditionalAudioLanguageInfos, VDM.AirLab.Sdk.Core",
    },
    {
      label: t("common:assetTypeUpperCase"),
      value: "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetAssetTypeInfos, VDM.AirLab.Sdk.Core",
    },
    {
      label: t("field:label.burnedSubtitle"),
      value: "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetBurnedSubtitleInfos, VDM.AirLab.Sdk.Core",
    },
    {
      label: t("field:label.contentMetadata"),
      value: "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetContentMetadataInfos, VDM.AirLab.Sdk.Core",
    },
    {
      label: t("common:assetMetadata"),
      value: "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetAssetMetadataInfos, VDM.AirLab.Sdk.Core",
    },
    {
      label: t("common:recentTitle"),
      value: "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetRecentTitleInfos, VDM.AirLab.Sdk.Core",
    },
    {
      label: t("common:videoCodec"),
      value: "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetVideoCodecInfos, VDM.AirLab.Sdk.Core",
    },
    {
      label: t("field:label.videoProfile"),
      value: "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetVideoProfileInfos, VDM.AirLab.Sdk.Core",
    },
    {
      label: t("field:label.durationMinutes"),
      value: "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetDurationInfos, VDM.AirLab.Sdk.Core",
    },
  ]);
  const enqueueCustomSnackbar = useCustomSnackbar();

  useEffect(() => {
    let updateMetas = {
      asset: metadataFromStorage.filter((f) => f.objectType == "DbAssetObjectInfos" && f.layer === "Asset"),
      content: metadataFromStorage.filter((f) => f.objectType == "DbAssetObjectInfos" && f.layer === "Content"),
      title: metadataFromStorage.filter((f) => f.objectType == "DbAssetObjectInfos" && f.layer === "Title"),
    };

    // Fix type
    // for (let layer in updateMetas)
    //   if (layer[0] !== "$")
    //     for (const field in updateMetas[layer])
    //       updateMetas[layer][field].$type =
    //         "VDM.AirLab.Sdk.Core.Database.Types." + updateMetas[layer][field].baseType + ", VDM.AirLab.Sdk.Core";

    setMetas(updateMetas);

    setupService.GetBroadcasterEnums().then((data) => {
      if (data.transkodeVideoProfiles) setVideoProfiles(data.transkodeVideoProfiles);
      if (data.transkodeVideoCodecs) setVideoCodecs(data.transkodeVideoCodecs);
      if (data.assetTypes) setAssetTypes(data.assetTypes);
      if (data.currencies) setCurrencies(data.currencies);
      if (data.orderProductTypes) setProductTypes(data.orderProductTypes);
    });
    userService.GetGroupsList().then((data) => {
      if (data.groups) setGroups(data.groups);
    });
  }, []);

  const handleCancelClick = () => {
    closeModal();
  };

  /// Return list of currencies unused in specified list
  const GetUnusedCurrencies = function (priceList) {
    if (currencies) {
      return currencies.filter(
        (c) => priceList === undefined || priceList === null || !priceList.map((p) => p.currency).includes(c)
      );
    } else return [];
  };

  const GetUnusedFilters = function (type) {
    var rule = updatedRuleSets.find((r) => r.$type === type);
    if (
      type === "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetContentMetadataInfos, VDM.AirLab.Sdk.Core"
    ) {
      if (!rule) return metas?.content;

      if (!rule.metadata) return metas?.content;

      return metas?.content.filter((f) => rule!.metadata.findIndex((m) => m.id === f.id) === -1);
    }

    if (
      type === "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetAssetMetadataInfos, VDM.AirLab.Sdk.Core"
    ) {
      if (!rule) return metas?.asset;

      if (!rule.metadata) return metas?.asset;

      return metas?.asset.filter((f) => rule!.metadata.findIndex((m) => m.id === f.id) === -1);
    }

    return null;
  };

  const handleConfirmClick = () => {
    let priceRule = {
      $type: "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleInfos, VDM.AirLab.Sdk.Core",
      platformId: newPriceRule.platformId,
      productType: newPriceRule.productType,
      name: newPriceRule.name,
      groupIds: newPriceRule.groupIds,
      price: newPriceRule.price,
      ruleSet: updatedRuleSets,
      computedPrice: newPriceRule.computedPrice,
      computedSummary: newPriceRule.computedSummary,
      id: -500,
    };

    switch (mode) {
      case "CREATE":
        orderService.CreatePriceRule(priceRule as DbOrderPriceRuleInfos).then((response) => {
          if (response.success) {
            enqueueCustomSnackbar(t("alert:success.priceRuleCreated"), "success");
          }
        });
        break;
      case "UPDATE":
        priceRule.id = newPriceRule.id;
        orderService.UpdatePriceRule(priceRule as DbOrderPriceRuleInfos).then((response) => {
          if (response.success) {
            enqueueCustomSnackbar(t("alert:success.priceRuleUpdated"), "success");
          }
        });
        break;
    }
    onConfirm && onConfirm();
    closeModal();
  };

  const handleMetadataUpdate = (
    id: number,
    value: any,
    row: any,
    valueIndex?: number,
    pushAndReplaceWhereCultureIds?: number[]
  ) => {
    setUpdatedRuleSets(
      produce((draft) => {
        let ruleSet = draft.find((x) => JSON.stringify(x) === JSON.stringify(row))?.metadata;
        let meta = ruleSet.find((x) => x.id === id);

        if (meta) {
          if (valueIndex === undefined) {
            if (pushAndReplaceWhereCultureIds === undefined) meta.value = value;
            else {
              // Considering the "cultureId" prop never changes
              meta.value = meta.value.filter((x) => !pushAndReplaceWhereCultureIds.includes(x.cultureId));
              value.forEach((element) => {
                meta.value.push(element);
              });
            }
          } else meta.value[valueIndex] = value;
        }
      })
    );
  };

  const IsValid = function () {
    // check price rule name is valid
    if (!newPriceRule.name || newPriceRule.name === "") return false;

    // check price rule has at least one price specified
    if (newPriceRule.price === null || newPriceRule.price === undefined || newPriceRule.price?.length === 0)
      return false;

    // check price rule has at least one condition
    if (updatedRuleSets === null || updatedRuleSets === undefined || updatedRuleSets.length === 0) return false;

    // check price rule conditions have at least one option price specified
    if (
      updatedRuleSets.some(
        (r) => r.isOptional && (r.optionPrice === null || r.optionPrice === undefined || r.optionPrice.length === 0)
      )
    )
      return false;

    // check content metadata condition has at least one metadata field
    if (
      updatedRuleSets.some(
        (r) =>
          r.$type ===
            "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetContentMetadataInfos, VDM.AirLab.Sdk.Core" &&
          (r.metadata == null || r.metadata.length === 0)
      )
    )
      return false;

    // check video codec condition has at least one video codec field
    if (
      updatedRuleSets.some(
        (r) =>
          r.$type ===
            "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetVideoCodecInfos, VDM.AirLab.Sdk.Core" &&
          (r.videoCodecs == null || r.videoCodecs.length === 0)
      )
    )
      return false;

    // check video profile condition has at least one video profile field
    if (
      updatedRuleSets.some(
        (r) =>
          r.$type ===
            "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetVideoProfileInfos, VDM.AirLab.Sdk.Core" &&
          (r.videoProfiles == null || r.videoProfiles.length === 0)
      )
    )
      return false;

    return true;
  };

  return (
    <Modal
      title={mode === "CREATE" ? t("modal:title.priceRuleCreation") : t("modal:title.priceRuleUpdate")}
      reference={ref}
      closeModal={handleCancelClick}
      width={1200}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button color="success" variant="outlined" onClick={handleConfirmClick} disabled={!IsValid()}>
            {t("common:confirm")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={3}>
        <FormSection icon={<ClearIcon />} defaultExpanded={true} title={t("form:section.title.configurePriceRule")}>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item md={6}>
              <FormControl sx={{ mb: 2, width: "100%" }}>
                <InputLabel shrink htmlFor="localized-title">
                  {t("common:name")}
                </InputLabel>
                <OutlinedInput
                  label={t("common:standard")}
                  size="medium"
                  value={newPriceRule.name}
                  onChange={(value) =>
                    setNewPriceRule(
                      produce((draft) => {
                        draft.name = value.target.value;
                      })
                    )
                  }
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ mb: 1, width: "100%" }}>
                <InputLabel htmlFor="emailList" sx={{ mb: 1 }}>
                  {t("field:label.productTypeLowerCase")}
                </InputLabel>
                <Autocomplete
                  options={productTypes}
                  value={newPriceRule.productType}
                  onChange={(e, value) =>
                    setNewPriceRule(
                      produce((draft) => {
                        draft.productType = value as OrderProductType;
                      })
                    )
                  }
                  renderInput={(params) => <TextField {...params} />}
                />
              </FormControl>
            </Grid>
          </Grid>

          <FormControl sx={{ mb: 2 }}>
            <InputLabel shrink htmlFor="localized-title">
              {t("common:groups")}
            </InputLabel>
            <Autocomplete
              multiple
              options={groups}
              getOptionLabel={(option) => option.group?.name || "-"}
              value={groups.filter((x) => newPriceRule.groupIds?.includes(x.id))}
              onChange={(e, value) =>
                setNewPriceRule(
                  produce((draft) => {
                    groups ? (draft.groupIds = value.map((x) => x.id)) : (draft.groupIds = []);
                  })
                )
              }
              renderInput={(params) => <TextField {...params} />}
            />
          </FormControl>

          <FormControl>
            <InputLabel sx={{ mb: 1 }}>{t("common:price")}</InputLabel>

            <AddPrice
              context="APART"
              price={GetUnusedCurrencies(newPriceRule.price)}
              options={newPriceRule.price}
              disabled={GetUnusedCurrencies(newPriceRule.price).length === 0 || !selectedCurrency}
              onCurrencySelection={(value) => {
                setSelectedCurrency(value);
              }}
              onCurrencyAdd={() => {
                setNewPriceRule(
                  produce((draft) => {
                    draft.price?.push({
                      currency: selectedCurrency as CurrencyTypes,
                      price: null,
                      priceDetails: null,
                      [immerable]: true,
                    });
                  })
                );
                setSelectedCurrency(null);
              }}
              onPriceAdd={(price, value) => {
                setNewPriceRule(
                  produce((draft) => {
                    let item = draft!.price?.find((x) => x.currency === price.currency);
                    if (item) item.price = parseInt(value.target.value);
                  })
                );
              }}
              onPriceRemove={(price) => {
                setNewPriceRule(
                  produce((draft) => {
                    if (draft.price) {
                      const index = draft.price.findIndex((x) => x.currency === price.currency);
                      if (index !== -1) draft.price?.splice(index, 1);
                    }
                  })
                );
              }}
            />
          </FormControl>
        </FormSection>
        <FormSection
          icon={<ClearIcon />}
          defaultExpanded={true}
          title={t("form:section.title.configurationConditions")}
        >
          {updatedRuleSets?.map((rule, index) => (
            <>
              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item md={1} sx={{ margin: "auto auto" }}>
                  <Switch
                    size="small"
                    value={rule.isOptional}
                    checked={rule.isOptional as boolean}
                    onChange={(e, value) => {
                      setUpdatedRuleSets(
                        produce((draft) => {
                          let item = draft.find((x) => JSON.stringify(x) === JSON.stringify(rule));
                          if (item) item.isOptional = value;
                        })
                      );
                    }}
                  />
                </Grid>
                <Grid item md={4} sx={{ margin: "auto auto" }}>
                  <AddPrice
                    context="TABLE"
                    price={GetUnusedCurrencies(rule.optionPrice)}
                    options={rule.optionPrice}
                    disabled={
                      GetUnusedCurrencies(rule.optionPrice).length === 0 ||
                      (rowCurrencySelected &&
                        !rowCurrencySelected.find((x) => JSON.stringify(x.row) === JSON.stringify(rule))?.curr)
                    }
                    onCurrencySelection={(value) => {
                      setRowCurrencySelected([...rowCurrencySelected, { row: rule, curr: value }]);
                    }}
                    onCurrencyAdd={() => {
                      setUpdatedRuleSets(
                        produce((draft) => {
                          let item = draft.find((x) => JSON.stringify(x) === JSON.stringify(rule));
                          if (item) {
                            if (item.optionPrice === null) item.optionPrice = [];
                            item.optionPrice?.push({
                              currency: rowCurrencySelected.find((x) => JSON.stringify(x.row) === JSON.stringify(rule))
                                ?.curr,
                              price: null,
                              priceDetails: null,
                              [immerable]: true,
                            });
                          }
                        })
                      );
                    }}
                    onPriceAdd={(price, value) => {
                      setUpdatedRuleSets(
                        produce((draft) => {
                          let item = draft.find((x) => JSON.stringify(x) === JSON.stringify(rule));
                          if (item) {
                            let newPrice = item.optionPrice?.find((x) => x.currency === price.currency);
                            if (newPrice) newPrice.price = parseInt(value.target.value);
                          }
                        })
                      );
                    }}
                    onPriceRemove={(price) => {
                      setUpdatedRuleSets(
                        produce((draft) => {
                          let item = draft.find((x) => JSON.stringify(x) === JSON.stringify(rule));
                          if (item) {
                            let index = item.optionPrice!.findIndex((x) => x.currency === price.currency);
                            if (index !== -1) {
                              item.optionPrice!.splice(index, 1);
                            }
                          }
                        })
                      );
                    }}
                  />
                </Grid>
                <Grid item md={2} sx={{ margin: "auto auto" }}>
                  {rule.$type ===
                    "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetContentMetadataInfos, VDM.AirLab.Sdk.Core" && (
                    <Typography variant="body2">{t("field:label.contentMetadata")}</Typography>
                  )}
                  {rule.$type ===
                    "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetAssetMetadataInfos, VDM.AirLab.Sdk.Core" && (
                    <Typography variant="body2">{t("common:assetMetadata")}</Typography>
                  )}
                  {rule.$type ===
                    "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetDurationInfos, VDM.AirLab.Sdk.Core" && (
                    <Typography variant="body2">{t("field:label.durationMinutes")}</Typography>
                  )}
                  {rule.$type ===
                    "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetVideoProfileInfos, VDM.AirLab.Sdk.Core" && (
                    <Typography variant="body2">{t("field:label.videoProfile")}</Typography>
                  )}
                  {rule.$type ===
                    "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetVideoCodecInfos, VDM.AirLab.Sdk.Core" && (
                    <Typography variant="body2">{t("common:videoCodec")}</Typography>
                  )}
                  {rule.$type ===
                    "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetBurnedSubtitleInfos, VDM.AirLab.Sdk.Core" && (
                    <Typography variant="body2">{t("common:burned")}</Typography>
                  )}
                  {rule.$type ===
                    "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleAdditionalAudioLanguageInfos, VDM.AirLab.Sdk.Core" && (
                    <Typography variant="body2">{t("common:audioLanguages")}</Typography>
                  )}
                  {rule.$type ===
                    "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetAssetTypeInfos, VDM.AirLab.Sdk.Core" && (
                    <Typography variant="body2">{t("common:assetTypeUpperCase")}</Typography>
                  )}
                  {rule.$type ===
                    "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetRecentTitleInfos, VDM.AirLab.Sdk.Core" && (
                    <Typography variant="body2">{t("common:recentTitle")}</Typography>
                  )}
                </Grid>
                <Grid item md={4} sx={{ margin: "auto auto" }}>
                  {(rule.$type ===
                    "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetContentMetadataInfos, VDM.AirLab.Sdk.Core" ||
                    rule.$type ===
                      "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetAssetMetadataInfos, VDM.AirLab.Sdk.Core") && (
                    <Stack spacing={1.5} width={"100%"}>
                      <Autocomplete
                        options={GetUnusedFilters(rule.$type) as DbMetadataFieldInfos[]}
                        onChange={(e, value) => {
                          setUpdatedRuleSets(
                            produce((draft) => {
                              let item = draft.find((x) => JSON.stringify(x) === JSON.stringify(rule));
                              if (item) {
                                if (!item.metadata || item.metadata === null) item.metadata = [];
                                item.metadata.push(value);
                              }
                            })
                          );
                        }}
                        renderInput={(params) => <TextField {...params} />}
                      />
                      {rule.metadata?.map((meta, index) => (
                        <>
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ height: "32px" }}
                          >
                            <InputLabel shrink sx={{ position: "relative" }}>
                              {meta.label}
                            </InputLabel>
                            <ClearIcon
                              color="error"
                              onClick={() => {
                                setUpdatedRuleSets(
                                  produce((draft) => {
                                    let item = draft.find((x) => JSON.stringify(x) === JSON.stringify(rule));
                                    if (item) {
                                      let index = item.metadata.findIndex((i) => i.id === meta.id);
                                      if (index !== -1) item.metadata.splice(index, 1);
                                    }
                                  })
                                );
                              }}
                            />
                          </Stack>
                          <MetadataInputIndex
                            key={index}
                            data={meta}
                            value={meta}
                            onChange={(v, fieldId, valueIndex, pushAndReplaceWhereCultureId) => {
                              handleMetadataUpdate(fieldId, v, rule, valueIndex, pushAndReplaceWhereCultureId);
                            }}
                          />
                        </>
                      ))}
                    </Stack>
                  )}

                  {rule.$type ===
                    "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetVideoProfileInfos, VDM.AirLab.Sdk.Core" && (
                    <Autocomplete
                      multiple
                      options={videoProfiles}
                      value={rule.videoProfiles || []}
                      onChange={(e, value) => {
                        setUpdatedRuleSets(
                          produce((draft) => {
                            let item = draft.find((x) => JSON.stringify(x) === JSON.stringify(rule));
                            if (item) item.videoProfiles = value;
                          })
                        );
                      }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  )}

                  {rule.$type ===
                    "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetVideoCodecInfos, VDM.AirLab.Sdk.Core" && (
                    <Autocomplete
                      multiple
                      options={videoCodecs}
                      value={rule.videoCodecs || []}
                      onChange={(e, value) => {
                        setUpdatedRuleSets(
                          produce((draft) => {
                            let item = draft.find((x) => JSON.stringify(x) === JSON.stringify(rule));
                            if (item) item.videoCodecs = value;
                          })
                        );
                      }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  )}

                  {rule.$type ===
                    "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetBurnedSubtitleInfos, VDM.AirLab.Sdk.Core" && (
                    <Checkbox
                      checked={rule.burned as boolean}
                      onChange={(e, value) => {
                        setUpdatedRuleSets(
                          produce((draft) => {
                            let item = draft.find((x) => JSON.stringify(x) === JSON.stringify(rule));
                            if (item) item.burned = value;
                          })
                        );
                      }}
                    />
                  )}

                  {rule.$type ===
                    "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleAdditionalAudioLanguageInfos, VDM.AirLab.Sdk.Core" && (
                    <OutlinedInput
                      label={t("common:standard")}
                      size="medium"
                      type="number"
                      value={rule.additionalLanguageRank}
                      onChange={(value) => {
                        setUpdatedRuleSets(
                          produce((draft) => {
                            let item = draft.find((x) => JSON.stringify(x) === JSON.stringify(rule));
                            if (item) item.additionalLanguageRank = parseInt(value.target.value);
                          })
                        );
                      }}
                    />
                  )}

                  {rule.$type ===
                    "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetAssetTypeInfos, VDM.AirLab.Sdk.Core" && (
                    <Autocomplete
                      multiple
                      options={assetTypes}
                      value={rule.types || []}
                      onChange={(e, value) => {
                        setUpdatedRuleSets(
                          produce((draft) => {
                            let item = draft.find((x) => JSON.stringify(x) === JSON.stringify(rule));
                            if (item) item.types = value;
                          })
                        );
                      }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  )}

                  {rule.$type ===
                    "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetRecentTitleInfos, VDM.AirLab.Sdk.Core" && (
                    <Checkbox
                      checked={rule.recentTitle as boolean}
                      onChange={(e, value) => {
                        setUpdatedRuleSets(
                          produce((draft) => {
                            let item = draft.find((x) => JSON.stringify(x) === JSON.stringify(rule));
                            if (item) item.recentTitle = value;
                          })
                        );
                      }}
                    />
                  )}

                  {rule.$type ===
                    "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleSetDurationInfos, VDM.AirLab.Sdk.Core" && (
                    <Stack>
                      <Autocomplete
                        options={["GreaterThan", "LowerThan"]}
                        sx={{ mb: 2 }}
                        value={rule.comparison}
                        onChange={(e, value) => {
                          setUpdatedRuleSets(
                            produce((draft) => {
                              let item = draft.find((x) => JSON.stringify(x) === JSON.stringify(rule));
                              if (item) item.comparison = value;
                            })
                          );
                        }}
                        renderInput={(params) => <TextField {...params} />}
                      />
                      <OutlinedInput
                        placeholder={t("field:placeholder.durationInMin")}
                        size="medium"
                        type="number"
                        value={rule.durationInMinutes}
                        onChange={(value) => {
                          setUpdatedRuleSets(
                            produce((draft) => {
                              let item = draft.find((x) => JSON.stringify(x) === JSON.stringify(rule));
                              if (item) item.durationInMinutes = parseInt(value.target.value);
                            })
                          );
                        }}
                      />
                    </Stack>
                  )}
                </Grid>
                <Grid item md={1} sx={{ margin: "auto auto" }}>
                  <ClearIcon
                    color="error"
                    onClick={() => {
                      setUpdatedRuleSets(
                        produce((draft) => {
                          let index = draft.findIndex((x) => JSON.stringify(x) === JSON.stringify(rule));
                          if (index !== -1) draft.splice(index, 1);
                        })
                      );
                    }}
                  />
                </Grid>
              </Grid>
              <Divider />
            </>
          ))}

          <Stack direction="row" justifyContent="flex-end" mt={2}>
            <Autocomplete
              options={ruleTypes}
              getOptionLabel={(option) => option.label}
              onChange={(e, value) => setSelectedCondition(value)}
              renderInput={(params) => <TextField sx={{ height: 30, mt: -3, ml: 1, width: 250 }} {...params} />}
            />
            <Button
              variant="contained"
              sx={{ ml: 2 }}
              onClick={() => {
                setUpdatedRuleSets(
                  produce((draft) => {
                    draft.push({
                      $type: selectedCondition.value,
                      isOptional: false,
                      optionPrice: null,
                    });
                  })
                );
                setSelectedCondition(null);
              }}
              disabled={selectedCondition === null}
            >
              {t("button:addCondition")}
            </Button>
          </Stack>
        </FormSection>
      </Stack>
    </Modal>
  );
});

export default ModalPriceRule;
